"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Agents = exports.Conversations = exports.keyFor = exports.ConversationPartialResults = exports.ConversationEvents = exports.ObjRecord = exports.Loadable = void 0;
const recoil_1 = require("recoil");
/*
    Loadable indicates a value can have a loading state
*/
class Loadable {
    constructor(state, value, error) {
        this.state = state;
        this.loading = state;
        this.value = value;
        this.error = error;
    }
    isLoaded() {
        return this.state === 'loaded';
    }
    isUnloaded() {
        return this.state === 'unloaded';
    }
    static error(err) {
        // eslint-disable-next-line no-console
        console.error('💥 Loadable error state:', err);
        return new Loadable('unloaded', undefined, err);
    }
    static loading() {
        return new Loadable('loading', undefined);
    }
    static loaded(value) {
        return new Loadable('loaded', value);
    }
    static unloaded() {
        return new Loadable('unloaded', undefined);
    }
    map(fn) {
        // Preserve error if present
        if (this.error != null) {
            return Loadable.error(this.error);
        }
        if (this.isLoaded() && this.value !== undefined) {
            try {
                const next = fn(this.value);
                return Loadable.loaded(next);
            }
            catch (err) {
                return Loadable.error(err);
            }
        }
        // Propagate non-loaded state forward with no value
        return new Loadable(this.state, undefined);
    }
}
exports.Loadable = Loadable;
/**
 * Simple object map keyed by id to support fast lookup
 * and convenient array-style operations.
 */
class ObjRecord {
    constructor(value) {
        this.value = value;
    }
    static of(items) {
        const mapping = {};
        for (const item of items) {
            mapping[item.id] = item;
        }
        return new ObjRecord(mapping);
    }
    static empty() {
        return new ObjRecord({});
    }
    get(id) {
        return this.value[id];
    }
    items() {
        return Object.values(this.value);
    }
    map(fn) {
        return this.items().map(fn);
    }
    ids() {
        return Object.keys(this.value);
    }
    without(id) {
        const filtered = this.items().filter((item) => item.id !== id);
        return ObjRecord.of(filtered);
    }
    with(item) {
        return ObjRecord.of([...this.items(), item]);
    }
    merge(other) {
        return ObjRecord.of([...this.items(), ...other.items()]);
    }
    toJSON() {
        return this.value;
    }
}
exports.ObjRecord = ObjRecord;
exports.ConversationEvents = (0, recoil_1.atom)({
    key: 'AgentApiEvents',
    default: {},
});
/**
 * ConversationPartialResults now stores metadata PER CONVERSATION,
 * with a nested map PER AGENT and an activeAgentId selector.
 *
 * Shape:
 * {
 *   [conversationId]: {
 *     activeAgentId: "agent-question-and-answer",
 *     byAgent: {
 *       "agent-start": { ...AgentMeta },
 *       "agent-question-and-answer": { ...AgentMeta }
 *     }
 *   }
 * }
 */
exports.ConversationPartialResults = (0, recoil_1.atom)({
    key: 'AgentApiConversationPartialResults',
    default: {},
});
/**
 * (Deprecated) composite-key helper retained for backward compatibility
 * with any legacy code still referencing it. Prefer using the nested map.
 */
const keyFor = (conversationId, agentId) => `${conversationId}:${agentId}`;
exports.keyFor = keyFor;
exports.Conversations = (0, recoil_1.atom)({
    key: 'AgentApiConversations',
    default: Loadable.unloaded(),
});
exports.Agents = (0, recoil_1.atom)({
    key: 'AgentApiAgents',
    default: Loadable.unloaded(),
});
